import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFormSubmissions } from '../../api/forms.api';
import type { FormFieldDefinition } from '@engage/types';
import { Btn, Icon, ICONS, PathChip, StatusChip } from '../../features/forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../features/forms/ui/tokens';

/**
 * Full-page review of one form's completed submissions: form fields as columns,
 * each submission a row. Reached from the Submissions count on the Forms list.
 */
export function FormSubmissionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: formDetail, isLoading: loadingForm } = useForm(id);
  const { data: submissions, isLoading: loadingSubs } = useFormSubmissions(id);

  const form = formDetail?.form;
  // Prefer the published version's fields (what the public form actually
  // renders), falling back to the working draft.
  const fields: FormFieldDefinition[] =
    formDetail?.published_version?.fields ?? formDetail?.current_version?.fields ?? [];

  // Column order = the form's fields, then any extra keys seen only in older
  // submissions (so historical answers aren't hidden after a field rename).
  const columns = useMemo(() => {
    const cols: { key: string; label: string }[] = fields.map((f) => ({ key: f.key, label: f.label }));
    const known = new Set(cols.map((c) => c.key));
    for (const s of submissions ?? []) {
      for (const key of Object.keys(s.field_values ?? {})) {
        if (!known.has(key)) {
          known.add(key);
          cols.push({ key, label: key });
        }
      }
    }
    return cols;
  }, [fields, submissions]);

  const rows = submissions ?? [];
  const isLoading = loadingForm || loadingSubs;

  return (
    <div style={{ padding: '26px 30px', fontFamily: FORM_UI_FONT, color: C.ink }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <button
            type="button"
            onClick={() => navigate('/forms')}
            style={{ fontSize: 12, color: C.sub, fontWeight: 500, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: FORM_UI_FONT, padding: 0 }}
          >
            Engage › Forms ›
          </button>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.indigo, margin: '4px 0 4px' }}>
            {form ? `${form.name} · Submissions` : 'Submissions'}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: C.sub }}>
            {form && <PathChip type={form.path_type} />}
            {form && <StatusChip status={form.status} />}
            <span>{isLoading ? 'Loading…' : `${rows.length} submission${rows.length === 1 ? '' : 's'}`}</span>
          </div>
        </div>
        <Btn kind="ghost" onClick={() => id && navigate(`/forms/${id}`)}>
          <Icon d={ICONS.eye} size={15} color={C.ink} />
          Open builder
        </Btn>
      </div>

      <div style={{ marginTop: 18 }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.sub }}>Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState />
        ) : (
          <SubmissionsTable columns={columns} rows={rows} />
        )}
      </div>
    </div>
  );
}

function SubmissionsTable({
  columns,
  rows,
}: {
  columns: { key: string; label: string }[];
  rows: { id: string; submitted_at: string; source: string; field_values: Record<string, unknown> }[];
}) {
  const cellStyle: React.CSSProperties = {
    padding: '12px 14px',
    fontSize: 12.5,
    color: C.ink,
    borderBottom: `1px solid ${C.line}`,
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  };
  const headStyle: React.CSSProperties = {
    padding: '12px 14px',
    fontSize: 11,
    fontWeight: 700,
    color: C.faint,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    textAlign: 'left',
    borderBottom: `1px solid ${C.line}`,
    whiteSpace: 'nowrap',
    background: C.bg,
    position: 'sticky',
    top: 0,
  };

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.line}`, overflow: 'auto', maxHeight: '70vh' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: FORM_UI_FONT }}>
        <thead>
          <tr>
            <th style={headStyle}>Submitted</th>
            <th style={headStyle}>Source</th>
            {columns.map((c) => (
              <th key={c.key} style={headStyle}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td style={{ ...cellStyle, color: C.sub }}>{new Date(s.submitted_at).toLocaleString()}</td>
              <td style={{ ...cellStyle, color: C.sub }}>{s.source}</td>
              {columns.map((c) => (
                <td key={c.key} style={cellStyle}>{formatValue(s.field_values?.[c.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ background: '#fff', borderRadius: 14, border: `2px dashed ${C.line}`, padding: '44px 28px', textAlign: 'center' }}>
      <Icon d={ICONS.forms} size={28} color={C.faint} />
      <h2 style={{ fontSize: 17, fontWeight: 800, color: C.indigo, margin: '12px 0 6px' }}>No submissions yet</h2>
      <p style={{ fontSize: 13, color: C.sub, margin: 0, maxWidth: 460, marginInline: 'auto' }}>
        Completed forms appear here. Each one also creates or updates a contact and is included in
        the owner's digest email.
      </p>
    </div>
  );
}

function formatValue(v: unknown): string {
  if (v == null || v === '') return '-';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
